import store from '../Redux/Store'
import {
  addToUpdatedFolders,
  removeFromCompressingFolder,
  removeFromUpdatingFolderProgress,
} from '../Redux/Slice/Dashboard/AlbumSlice'
import {
  showSuccessToast,
  showWarningToast,
  showErrorToast,
} from '../components/atoms/Utlis/Toast'
import { trackClarityEvent } from '../analytics/ClarityAnalytics'
import { CLARITY_EVENTS } from '../analytics/EventTypes'
import { resumeFolderImageUploadJob } from './folderImageUploadRunner'
import type { FolderUploadPipelineResult } from './folderImageUploadTypes'
import { deleteUploadJob, listIncompleteJobs } from './uploadJobPersistence'
import { ENABLE_UPLOAD_RESUME_ON_REFRESH } from './folderUploadConfig'

/** In-memory only — blocks React Strict Mode double-run in the same page load. */
const runningJobIds = new Set<string>()
let resumeInvocationPending = false

export function finalizeFolderUpload(payload: {
  folder_id: number
  project_id: number
  uploaded?: number
  failed?: number
  total?: number
  skippedDuplicates?: number
}) {
  store.dispatch(removeFromCompressingFolder(payload.folder_id))
  store.dispatch(removeFromUpdatingFolderProgress(payload.folder_id))
  store.dispatch(
    addToUpdatedFolders({
      folder_id: payload.folder_id,
      project_id: payload.project_id,
    }),
  )

  const { uploaded, failed, total } = payload
  const skipped = (payload as { skippedDuplicates?: number }).skippedDuplicates
  if (failed != null && failed > 0 && total != null && uploaded != null) {
    showWarningToast(
      `Uploaded ${uploaded} of ${total} images. ${failed} could not be uploaded — try those again.`,
    )
  } else if (skipped != null && skipped > 0) {
    showWarningToast(
      `Upload complete. ${skipped} file(s) were already in this folder and were not uploaded again.`,
    )
  } else {
    showSuccessToast('Folders updated successfully.')
  }

  trackClarityEvent(CLARITY_EVENTS.FOLDER_UPDATED, {
    folder_id: payload.folder_id,
    project_id: payload.project_id,
  })
  trackClarityEvent(CLARITY_EVENTS.IMAGE_UPLOADED, {
    folder_id: payload.folder_id,
    project_id: payload.project_id,
  })
}

async function runJob(jobId: string): Promise<void> {
  if (runningJobIds.has(jobId)) return
  runningJobIds.add(jobId)

  try {
    const result = await resumeFolderImageUploadJob(jobId)
    finalizeFolderUpload({
      folder_id: result.folderId,
      project_id: result.projectId,
      uploaded: result.uploaded,
      failed: result.failed,
      total: result.total,
      skippedDuplicates: result.skippedDuplicates,
    })
  } catch (error: unknown) {
    const err = error as Error & {
      pipelineResult?: FolderUploadPipelineResult & {
        folderId: number
        projectId: number
      }
    }
    const partial = err.pipelineResult
    // If the job permanently failed, clean it up from IndexedDB so it doesn't get stuck in an endless resume loop
    await deleteUploadJob(jobId)
    if (partial && partial.uploaded > 0) {
      finalizeFolderUpload({
        folder_id: partial.folderId,
        project_id: partial.projectId,
        uploaded: partial.uploaded,
        failed: partial.failed,
        total: partial.total,
        skippedDuplicates: partial.skippedDuplicates,
      })
    } else {
      if (partial?.folderId) {
        store.dispatch(removeFromCompressingFolder(partial.folderId))
        store.dispatch(removeFromUpdatingFolderProgress(partial.folderId))
      }
      const alreadyNotified = Boolean(
        (error as any)?.hasShownToast ||
          (partial?.failedUploadDetails &&
            partial.failedUploadDetails.length > 0),
      )
      if (!alreadyNotified) {
        showErrorToast(
          err?.message ||
            'Upload stopped. Please try again.',
        )
      }
    }
  } finally {
    runningJobIds.delete(jobId)
  }
}

/**
 * Resume uploads saved in IndexedDB after page refresh.
 * Disabled when ENABLE_UPLOAD_RESUME_ON_REFRESH is false.
 */
export async function resumePendingFolderUploads(): Promise<void> {
  if (!ENABLE_UPLOAD_RESUME_ON_REFRESH) {
    return
  }

  const token =
    store.getState().auth.access_token ||
    localStorage.getItem('access_token')
  if (!token) return

  const jobs = await listIncompleteJobs()
  if (jobs.length === 0) return

  showWarningToast(
    jobs.length === 1
      ? 'Continuing folder upload in the background…'
      : `Continuing ${jobs.length} folder uploads in the background…`,
  )

  for (const job of jobs) {
    void runJob(job.jobId)
  }
}

/**
 * Called from Dashboard on load to resume interrupted uploads.
 * Disabled when ENABLE_UPLOAD_RESUME_ON_REFRESH is false.
 */
export function scheduleResumePendingFolderUploads(): void {
  if (!ENABLE_UPLOAD_RESUME_ON_REFRESH) {
    return
  }

  if (resumeInvocationPending) return
  resumeInvocationPending = true
  void resumePendingFolderUploads()
}
